// Spec: specs/admin/panel/entity-detail-views/functional_requirements.md §3.8, §8.10
//       specs/account/billing-info/functional_requirements.md §5

// The provider's inline card entry for the legacy surfaces — the survey checkout family
// (through the invoice-table payment selector) and the ngAdmin payment-methods directive,
// which mounts it by registry name through the React bridge. It carries the callback
// contract the hosted-lightbox component had, so its callers change only by prop names,
// and it sits on the shared client rather than on a second loader.

import PropTypes from 'prop-types'
import { Map } from 'immutable'
import { useCallback, useRef, useState } from 'react'

import CardEntryFields from '@aytm/app/payment-methods/components/CardEntryFields'
import { useStripe } from '@aytm/app/payment-methods/hooks/useStripe'
import useMountedRef from '@aytm/lib/hooks/useMountedRef'
import Button from '@aytm/lib/rfd-ui/components/basic/button'
import MaterialTextField from '@aytm/lib/rfd-ui/components/basic/material-text-field'
import StripeLockSvg from '@aytm/lib/rfd-ui/images/stripe/stripe-lock.svg'

import '@aytm/lib/rfd-ui/styles/payment/credit-card-entry.scss'

const ERROR_EMPTY_TITLE = 'Please enter a title for your payment method.'

export default function CreditCardEntry({
  stripeKey,
  email,
  title: initialTitle,
  submitLabel = 'ADD CREDIT CARD',
  width,
  testId,
  onSave,
  onCancel
}) {
  const stripe = useStripe({ stripeKey, email })
  const mountedRef = useMountedRef()
  const titleInput = useRef(null)
  const [title, setTitle] = useState(initialTitle || '')
  // one value rather than two flags: the capture-to-persist handover has to be a single
  // transition, or the form re-arms itself for a render in between
  const [phase, setPhase] = useState('idle')
  const [error, setError] = useState(null)
  // bumped by every attempt and by every cancel — a caller's cancel need not unmount us, so
  // only a stale value tells a returning result that the form it belonged to is gone
  const attemptRef = useRef(0)
  const saving = 'idle' !== phase
  const persisting = 'persisting' === phase

  const persist = useCallback(
    (reference, attempt) => {
      const settle = saved => {
        if (!mountedRef.current || attempt !== attemptRef.current) return
        setPhase('idle')
        // the capture consumed the session, so a form left open for another attempt needs a new one
        if (true !== saved) stripe.capture.ensureSession()
      }
      setPhase('persisting')
      const willSave = onSave(Map({ title, token: reference.id }), Promise)
      // a caller that answers with no promise drove the save itself and owns the form's fate; settling
      // here would re-arm the submit and mint a replacement session over a capture already in flight
      if (null == willSave || 'function' != typeof willSave.then) return
      willSave.then(settle, () => settle(false))
    },
    [mountedRef, onSave, stripe, title]
  )

  const submit = useCallback(() => {
    if (saving) return
    if (!title.trim()) {
      setError(ERROR_EMPTY_TITLE)
      if (titleInput.current) titleInput.current.focus()
      return
    }

    setError(null)
    const attempt = ++attemptRef.current
    setPhase('capturing')
    stripe.openCheckoutWith('add', result => {
      if (!mountedRef.current || attempt !== attemptRef.current) return
      if (false === result.ok) {
        setPhase('idle')
        setError(result.error)
        return
      }
      persist(result.reference, attempt)
    })
  }, [mountedRef, persist, saving, stripe, title])

  const cancel = useCallback(() => {
    attemptRef.current += 1
    setPhase('idle')
    onCancel()
  }, [onCancel])

  return (
    <div className="rfd-ui-credit-card-entry" style={{ width }} data-testid={testId}>
      <div className="rfd-ui-credit-card-entry-form">
        <div className="rfd-ui-credit-card-entry-row">
          <MaterialTextField
            placeholder='Give it a title (e.g. "Marketing credit card")'
            className="rfd-ui-credit-card-entry-title"
            width="100%"
            ref={titleInput}
            value={title}
            readOnly={saving}
            onChange={value => {
              setTitle(value)
              setError(null)
            }}
          />
        </div>
        <div className="rfd-ui-credit-card-entry-row">
          <div style={{ width: '100%' }}>
            <CardEntryFields capture={stripe.capture} testId={`${testId || 'credit-card-entry'}-fields`} />
          </div>
        </div>
        <div className="rfd-ui-credit-card-entry-row">
          <img alt="" className="rfd-ui-credit-card-entry-icon" src={StripeLockSvg} />
          <div className="rfd-ui-credit-card-entry-description">
            Securely add your payment information directly to our payment processing company, Stripe, using SSL
            encrypted connection.
            <br />
            We use PCI compliant technology to keep you safe.
            <br />
            We never see nor store your full billing info on our servers.
          </div>
        </div>
        {error && (
          <div className="rfd-ui-credit-card-entry-row">
            <div
              className="rfd-ui-credit-card-entry-text"
              role="alert"
              style={{ color: '#d9534f' }}
              data-testid={`${testId || 'credit-card-entry'}-error-message`}
            >
              {error}
            </div>
          </div>
        )}
        <div className="rfd-ui-credit-card-entry-buttons">
          {onCancel && (
            <Button className="light-gray cancel-button" label="CANCEL" disabled={persisting} onAction={cancel} />
          )}
          <Button
            label={submitLabel}
            disabled={!stripe.isLoaded || saving}
            onAction={submit}
            width={240}
            icon={saving ? 'icon-aytmfd-spin afd-spin' : null}
          />
        </div>
      </div>
    </div>
  )
}

CreditCardEntry.propTypes = {
  stripeKey: PropTypes.string.isRequired,
  email: PropTypes.string,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  testId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func
}
